import { useSyncExternalStore } from 'react'
import { sessionStore } from '@/core/auth/SessionStore'
import { aquecerContextos, baixarPacoteParteDiaria } from '@/features/parteDiaria/parteDiariaOffline'
import { aquecerCarreteiro } from '@/features/carreteiro/carreteiroOffline'
import { hojeSP } from '@/features/parteDiaria/parteDiariaRegras'

/**
 * Pré-carga offline — roda em segundo plano depois de cada ciclo de sync com
 * rede. Catálogos gerais (Produção/Fotos) já vão para o Dexie via catalogSync;
 * aqui entram as listas que dependem de chefe/obra/caminhão/data:
 *
 *  - Parte Diária: pacote único (1 request) a cada 30 min + contexto de hoje
 *    das MVEs em uso.
 *  - Carreteiro: chefe/caminhões/serviços das obras do projeto selecionado,
 *    uma vez por dia por projeto (ou a cada 6h), porque custa ~1 request por
 *    obra + 1 por caminhão.
 *
 * O estado fica no localStorage para a Home dizer ao operador, ANTES de ele
 * sair para o campo, se o aparelho está pronto para trabalhar sem sinal.
 */

const KEY = 'monitor.offlineReady'
const PD_INTERVALO_MS = 30 * 60 * 1000
const CARR_INTERVALO_MS = 6 * 60 * 60 * 1000

export interface OfflineReadyState {
  userId: number | null
  /** Parte Diária */
  pdAt: number | null
  pdStatus: 'ok' | 'sem_acesso' | 'falha' | null
  pdResumo: string | null
  /** Carreteiro */
  carrAt: number | null
  carrDt: string | null
  carrProjetoId: number | null
  carrStatus: 'ok' | 'parcial' | 'falha' | null
  carrResumo: string | null
}

const VAZIO: OfflineReadyState = {
  userId: null,
  pdAt: null,
  pdStatus: null,
  pdResumo: null,
  carrAt: null,
  carrDt: null,
  carrProjetoId: null,
  carrStatus: null,
  carrResumo: null,
}

let estado: OfflineReadyState = ler()
let rodando = false
const ouvintes = new Set<() => void>()
let snapshot: { estado: OfflineReadyState; rodando: boolean } = { estado, rodando }

function ler(): OfflineReadyState {
  try {
    const raw = localStorage.getItem(KEY)
    return raw ? { ...VAZIO, ...(JSON.parse(raw) as Partial<OfflineReadyState>) } : VAZIO
  } catch {
    return VAZIO
  }
}

function publicar(parcial: Partial<OfflineReadyState>, novoRodando = rodando) {
  estado = { ...estado, ...parcial }
  rodando = novoRodando
  snapshot = { estado, rodando }
  try {
    localStorage.setItem(KEY, JSON.stringify(estado))
  } catch {
    /* storage cheio */
  }
  ouvintes.forEach((l) => l())
}

export function useOfflineReady(): { estado: OfflineReadyState; rodando: boolean } {
  return useSyncExternalStore(
    (cb) => {
      ouvintes.add(cb)
      return () => ouvintes.delete(cb)
    },
    () => snapshot,
    () => snapshot,
  )
}

let promessa: Promise<void> | null = null

/** Idempotente e com mutex. `forcar` ignora os intervalos (botão "Atualizar agora"). */
export function aquecerOffline(opts: { forcar?: boolean } = {}): Promise<void> {
  if (promessa) return promessa
  promessa = executar(opts.forcar === true).finally(() => {
    promessa = null
  })
  return promessa
}

async function executar(forcar: boolean): Promise<void> {
  const sessao = sessionStore.current()
  if (!sessao || sessionStore.serverRejectedToken()) return
  // Outro usuário neste aparelho: o estado anterior não vale.
  if (estado.userId !== sessao.userId) publicar({ ...VAZIO, userId: sessao.userId })

  const agora = Date.now()
  const hoje = hojeSP()
  const fazerPd = forcar || estado.pdAt == null || agora - estado.pdAt > PD_INTERVALO_MS || estado.pdStatus === 'falha'
  const projetoId = sessao.projetoAtualId
  const fazerCarr =
    projetoId != null &&
    (forcar ||
      estado.carrProjetoId !== projetoId ||
      estado.carrDt !== hoje ||
      estado.carrAt == null ||
      agora - estado.carrAt > CARR_INTERVALO_MS ||
      estado.carrStatus !== 'ok')
  if (!fazerPd && !fazerCarr) return

  publicar({}, true)
  try {
    if (fazerPd) {
      const r = await baixarPacoteParteDiaria()
      if (r.status === 'ok') {
        const ctx = await aquecerContextos(r.mves)
        publicar({
          pdAt: Date.now(),
          pdStatus: 'ok',
          pdResumo: `${r.mves.length} equipamento(s), ${r.obras} obra(s), ${ctx.total} contexto(s) de hoje`,
        })
      } else if (r.status === 'sem_acesso') {
        publicar({ pdAt: Date.now(), pdStatus: 'sem_acesso', pdResumo: null })
      } else {
        publicar({ pdStatus: 'falha', pdResumo: r.message })
      }
    }
    if (fazerCarr && projetoId != null) {
      const r = await aquecerCarreteiro(projetoId, sessao.obraAtualId)
      publicar({
        carrAt: r.status === 'ok' ? Date.now() : estado.carrAt,
        carrDt: r.status === 'ok' ? hoje : estado.carrDt,
        carrProjetoId: projetoId,
        carrStatus: r.status,
        carrResumo: r.status === 'ok' ? `${r.obras} obra(s), ${r.mves} caminhão(ões)` : (r.message ?? null),
      })
    }
  } finally {
    publicar({}, false)
  }
}
